import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'

type Pagina = {
  name: string; destination: string | null; hotel: string | null
  begin_date: string | null; end_date: string | null
  price_info: string | null; notes: string | null
  capacity: number | null; locuri_ramase: number | null
  signups_open: boolean; motiv_inchis: string | null
}

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' }) : null

/** Pagina publica a grupului: singurul ecran fara autentificare. Nu arata datele
 *  nimanui — doar calatoria si formularul. */
export function InscriereGrup() {
  const { token = '' } = useParams()
  const [f, setF] = useState({ full_name: '', phone: '', email: '', persons: '2', notes: '' })
  const [eroare, setEroare] = useState<string | null>(null)
  const s = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }))

  const { data, isPending, isError } = useQuery({
    queryKey: ['grup-public', token],
    queryFn: () => api.get<Pagina>(`/groups/public/${token}`),
    retry: false,
  })

  const trimite = useMutation({
    mutationFn: () => {
      if (f.full_name.trim().length < 3) throw new ApiError(400, 'Scrie numele complet')
      if (!f.phone.trim() && !f.email.trim()) throw new ApiError(400, 'Lasa un telefon sau un email')
      return api.post<{ message: string }>(`/groups/public/${token}/inscriere`, {
        full_name: f.full_name, phone: f.phone || undefined, email: f.email || undefined,
        persons: Number(f.persons) || 1, notes: f.notes || undefined,
      })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu am putut trimite'),
  })

  const camp = 'h-11 w-full px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[15px] focus:border-accent outline-none'
  const eticheta = 'text-[13px] text-muted mb-1 block'

  if (isPending) return <Ecran><p className="text-muted">Se incarca…</p></Ecran>
  if (isError || !data) return (
    <Ecran>
      <h1 className="text-[20px] font-semibold">Link invalid</h1>
      <p className="mt-2 text-muted">Linkul nu mai este valabil. Contacteaza agentia.</p>
    </Ecran>
  )

  return (
    <Ecran>
      <p className="text-[12px] uppercase tracking-[0.14em] text-muted">Inscriere in grup</p>
      <h1 className="mt-1 text-[26px] font-semibold tracking-tight">{data.name}</h1>
      <div className="mt-3 space-y-1 text-[15px]">
        {data.destination && <p>{data.destination}{data.hotel ? ` · ${data.hotel}` : ''}</p>}
        {data.begin_date && (
          <p className="text-muted">
            {zi(data.begin_date)}{data.end_date ? ` – ${zi(data.end_date)}` : ''}
          </p>
        )}
        {data.price_info && <p className="font-medium">{data.price_info}</p>}
        {data.locuri_ramase !== null && (
          <p className="text-muted tnum">{data.locuri_ramase} locuri ramase</p>
        )}
      </div>
      {data.notes && <p className="mt-4 text-[14.5px] text-ink-soft whitespace-pre-line">{data.notes}</p>}

      <div className="mt-7 pt-7 border-t border-line">
        {trimite.isSuccess ? (
          <div className="rounded-[var(--radius-card)] bg-good-soft p-5">
            <p className="text-[16px] font-medium text-good">{trimite.data.message}</p>
            <p className="mt-1 text-[14px] text-ink-soft">Nu mai trebuie sa trimiti nimic.</p>
          </div>
        ) : !data.signups_open ? (
          <div className="rounded-[var(--radius-card)] bg-page p-5">
            <p className="text-[15px] font-medium">Inscrierile sunt inchise</p>
            <p className="mt-1 text-[14px] text-muted">{data.motiv_inchis}</p>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setEroare(null); trimite.mutate() }}>
            <div>
              <label className={eticheta} htmlFor="nume">Nume si prenume</label>
              <input id="nume" className={camp} value={f.full_name} onChange={s('full_name')}
                autoComplete="name" required />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={eticheta} htmlFor="tel">Telefon</label>
                <input id="tel" type="tel" className={camp} value={f.phone} onChange={s('phone')}
                  autoComplete="tel" />
              </div>
              <div>
                <label className={eticheta} htmlFor="mail">Email</label>
                <input id="mail" type="email" className={camp} value={f.email} onChange={s('email')}
                  autoComplete="email" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={eticheta} htmlFor="pers">Cate persoane</label>
                <input id="pers" type="number" min={1} max={40} className={camp}
                  value={f.persons} onChange={s('persons')} />
              </div>
            </div>
            <div>
              <label className={eticheta} htmlFor="obs">Observatii (optional)</label>
              <textarea id="obs" rows={3} value={f.notes} onChange={s('notes')}
                className={camp.replace('h-11', 'py-2.5 min-h-[84px]')} />
            </div>
            <button type="submit" disabled={trimite.isPending}
              className="h-11 px-5 rounded-[var(--radius-control)] bg-accent text-white text-[15px] font-medium hover:bg-accent-ink disabled:opacity-50">
              {trimite.isPending ? 'Se trimite…' : 'Ma inscriu'}
            </button>
            {eroare && <p className="text-[13.5px] text-bad">{eroare}</p>}
            <p className="text-[12.5px] text-muted">
              Trimiti doar numele si datele de contact. Actele (CI/pasaport) se completeaza
              mai tarziu, direct cu agentul.
            </p>
          </form>
        )}
      </div>
    </Ecran>
  )
}

function Ecran({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-page py-10 px-5">
      <div className="mx-auto max-w-xl bg-surface border border-line rounded-[var(--radius-card)] p-7 sm:p-9">
        {children}
        <p className="mt-8 pt-5 border-t border-line text-[12.5px] text-muted">
          Intertur · agentie de turism
        </p>
      </div>
    </div>
  )
}
